import type { SxProps, Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import type { PinCellStyle } from '../../components/pinSetup/PinCodeField'

export const LOGIN_FONTS = {
    sans: '"Work Sans", sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
} as const

export const LOGIN_ACCENT = 'rgb(86, 153, 239)'
/** Accent for text, links, borders and fills on white: #5699EF is only ~2.9:1 there. */
export const LOGIN_ACCENT_INK = 'rgb(30, 95, 180)'
/** Official Google Sign-In outline button label color */
export const LOGIN_GOOGLE_TEXT = '#1F1F1F'

export type LoginPalette = {
    bg: string
    surface: string
    surface2: string
    border: string
    /** Accent for text, links, borders and focus rings (darker ink in light mode for contrast). */
    accent: string
    accentSoft: string
    fg: string
    /** Secondary text tier. */
    secondary: string
    /** Lowest tier still used for real text (AA on the panel background). */
    mute: string
}

export function getLoginPalette(isDark: boolean): LoginPalette {
    if (isDark) {
        return {
            bg: 'rgb(14, 14, 14)',
            surface: 'rgb(22, 22, 22)',
            surface2: 'rgb(32, 32, 32)',
            border: 'rgb(58, 58, 58)',
            accent: LOGIN_ACCENT,
            accentSoft: 'rgba(86, 153, 239, 0.14)',
            fg: 'rgb(232, 232, 232)',
            secondary: 'rgba(255, 255, 255, 0.7)',
            mute: 'rgb(140, 140, 140)',
        }
    }
    return {
        bg: '#ffffff',
        surface: '#f6f6f6',
        surface2: '#eeeeee',
        border: '#d8d8d8',
        accent: LOGIN_ACCENT_INK,
        accentSoft: 'rgba(86, 153, 239, 0.10)',
        fg: 'rgb(38, 38, 38)',
        secondary: 'rgb(88, 88, 88)',
        mute: 'rgb(108, 108, 108)',
    }
}

export function loginGhostButtonSx(palette: LoginPalette): SxProps<Theme> {
    return {
        borderRadius: '4px !important',
        minHeight: '40px !important',
        maxHeight: '40px !important',
        border: `1px solid ${palette.border} !important`,
        backgroundColor: `${palette.bg === '#ffffff' ? '#ffffff' : palette.surface} !important`,
        color: `${palette.bg === '#ffffff' ? LOGIN_GOOGLE_TEXT : palette.fg} !important`,
        textTransform: 'none',
        boxShadow: 'none !important',
        fontSize: '14px',
        fontWeight: 500,
        '&:hover': {
            borderColor: `${palette.accent} !important`,
            backgroundColor: `${palette.bg === '#ffffff' ? '#ffffff' : palette.surface2} !important`,
        },
    }
}

export const loginPrimaryButtonSx: SystemStyleObject<Theme> = {
    borderRadius: '4px !important',
    minHeight: '40px !important',
    maxHeight: '40px !important',
    backgroundColor: `${LOGIN_ACCENT} !important`,
    // Near-black label: white on #5699EF is only ~2.9:1.
    color: 'rgb(8, 8, 8) !important',
    boxShadow: 'none !important',
    border: 'none !important',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    '&:hover': {
        backgroundColor: '#ffffff !important',
        color: 'rgb(8, 8, 8) !important',
        boxShadow: 'none !important',
    },
    '&.Mui-disabled': {
        backgroundColor: 'rgba(86, 153, 239, 0.35) !important',
        color: 'rgba(8, 8, 8, 0.6) !important',
    },
    // Light mode: darker fill keeps white text at 6.3:1; hover darkens instead of going white on a white page.
    '.login-page-light &': {
        backgroundColor: `${LOGIN_ACCENT_INK} !important`,
        color: '#fff !important',
    },
    '.login-page-light &:hover': {
        backgroundColor: 'rgb(22, 78, 150) !important',
        color: '#fff !important',
    },
    '.login-page-light &.Mui-disabled': {
        backgroundColor: 'rgba(30, 95, 180, 0.35) !important',
        color: 'rgba(255, 255, 255, 0.8) !important',
    },
}

/** Text actions across the login flows (Sign Up, Forgot password, Logout, Resend…): accent, medium weight, primary on hover. */
export function loginLinkSx(palette: LoginPalette): SxProps<Theme> {
    return {
        color: palette.accent,
        fontWeight: 500,
        textDecoration: 'none',
        cursor: 'pointer',
        fontSize: '0.875rem',
        '&:hover': { color: palette.fg, textDecoration: 'none' },
    }
}

/** PIN cells for PinCodeField: 'large' on the unlock screen, 'compact' in setup / forgot-PIN forms. */
export function loginPinCellStyle(palette: LoginPalette, size: 'large' | 'compact' = 'large'): PinCellStyle {
    const large = size === 'large'
    return {
        height: large ? 56 : 40,
        gap: large ? 12 : 8,
        radius: large ? 6 : 4,
        fontSize: large ? '1.5rem' : '1.15rem',
        fontFamily: LOGIN_FONTS.mono,
        background: palette.surface,
        border: palette.border,
        borderWidth: large ? 1.5 : 1,
        accent: palette.accent,
        focusWidth: 2,
        color: palette.fg,
    }
}
